import React, { useState, useEffect } from 'react';
import { 
  Wrench, 
  Clock, 
  AlertTriangle, 
  ArrowLeft,
  Lock,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

const MaintenancePage: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState({
    message: '',
    estimatedTime: '',
    contact: '',
    title: 'Sistema em Manutenção',
    subtitle: 'Estamos trabalhando para melhorar sua experiência',
    backgroundColor: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
    cardColor: 'rgba(255, 255, 255, 0.1)',
    textColor: '#ffffff',
    accentColor: '#f59e0b',
    logoUrl: '/images/LOGO.png',
    animationEnabled: true,
    customCss: ''
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  // Detectar Ctrl + ' (aspas)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "'") {
        event.preventDefault();
        setShowLogin(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown, { passive: true });
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Carregar informações de manutenção do Firebase
  useEffect(() => {
    const maintenanceRef = doc(db, 'system', 'maintenance');
    
    const unsubscribe = onSnapshot(maintenanceRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        setMaintenanceInfo({
          message: data.message || '',
          estimatedTime: data.estimatedTime || '',
          contact: data.contact || '',
          title: data.title || 'Sistema em Manutenção',
          subtitle: data.subtitle || 'Estamos trabalhando para melhorar sua experiência',
          backgroundColor: data.backgroundColor || 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
          cardColor: data.cardColor || 'rgba(255, 255, 255, 0.1)',
          textColor: data.textColor || '#ffffff',
          accentColor: data.accentColor || '#f59e0b',
          logoUrl: data.logoUrl || '/images/LOGO.png',
          animationEnabled: data.animationEnabled !== false,
          customCss: data.customCss || ''
        });
      }
    }, (error) => {
      console.error('Erro ao carregar informações de manutenção:', error);
    });

    return () => unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');

    try {
      await login(loginData.email, loginData.password);
      // Login bem-sucedido, redirecionar para o sistema
      navigate('/admin/contratos');
    } catch (error) {
      setLoginError('Email ou senha incorretos');
    }

    setIsLoggingIn(false);
  };


  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: maintenanceInfo.backgroundColor,
        color: maintenanceInfo.textColor
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: maintenanceInfo.customCss }} />
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-12">
          {/* Logo */}
          <div className="mb-8">
            <img 
              src={maintenanceInfo.logoUrl} 
              alt="PlayKids - Contratos" 
              className="w-32 h-auto mx-auto mb-6"
            />
          </div>
          
          {/* Ícone de Manutenção */}
          <div 
            className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 shadow-2xl ${
              maintenanceInfo.animationEnabled ? 'animate-[maintenancePulse_3s_ease-in-out_infinite]' : ''
            }`}
            style={{ 
              background: `linear-gradient(135deg, ${maintenanceInfo.accentColor} 0%, ${maintenanceInfo.accentColor}dd 100%)`
            }}
          >
            <Wrench className="w-10 h-10 text-white" />
          </div>
          
          <h1 className="text-4xl font-bold mb-4" style={{ color: maintenanceInfo.textColor }}>
            {maintenanceInfo.title}
          </h1>
          <p className="text-lg opacity-80" style={{ color: maintenanceInfo.textColor }}>
            {maintenanceInfo.subtitle}
          </p>
        </div>

        {/* Card Principal */}
        <div 
          className="backdrop-blur-md rounded-2xl p-8 border border-white/20 shadow-2xl"
          style={{ 
            backgroundColor: maintenanceInfo.cardColor,
            color: maintenanceInfo.textColor
          }}
        >
          {/* Status */}
          <div className="flex items-center gap-3 mb-6">
            <div 
              className="h-12 w-12 rounded-lg flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${maintenanceInfo.accentColor} 0%, ${maintenanceInfo.accentColor}dd 100%)`
              }}
            >
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold" style={{ color: maintenanceInfo.textColor }}>
                Manutenção em Andamento
              </h2>
              <p className="opacity-80" style={{ color: maintenanceInfo.textColor }}>
                Nossos técnicos estão trabalhando no sistema
              </p>
            </div>
          </div>

          {/* Informações */}
          <div className="space-y-4 mb-8">
            {/* Tempo Estimado - só aparece se não estiver vazio */}
            {maintenanceInfo.estimatedTime && maintenanceInfo.estimatedTime.trim() !== '' && (
              <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all duration-300">
                <Clock className="w-5 h-5" style={{ color: maintenanceInfo.accentColor }} />
                <div>
                  <p className="font-medium" style={{ color: maintenanceInfo.textColor }}>Tempo Estimado</p>
                  <p className="opacity-80" style={{ color: maintenanceInfo.textColor }}>{maintenanceInfo.estimatedTime}</p>
                </div>
              </div>
            )}

            {/* Motivo - só aparece se não estiver vazio */}
            {maintenanceInfo.message && maintenanceInfo.message.trim() !== '' && (
              <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all duration-300">
                <AlertTriangle className="w-5 h-5" style={{ color: maintenanceInfo.accentColor }} />
                <div>
                  <p className="font-medium" style={{ color: maintenanceInfo.textColor }}>Motivo</p>
                  <p className="opacity-80" style={{ color: maintenanceInfo.textColor }}>{maintenanceInfo.message}</p>
                </div>
              </div>
            )}

            {/* Contato - só aparece se não estiver vazio */}
            {maintenanceInfo.contact && maintenanceInfo.contact.trim() !== '' && (
              <div className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 transition-all duration-300">
                <ArrowLeft className="w-5 h-5" style={{ color: maintenanceInfo.accentColor }} />
                <div>
                  <p className="font-medium" style={{ color: maintenanceInfo.textColor }}>Contato</p>
                  <p className="opacity-80" style={{ color: maintenanceInfo.textColor }}>{maintenanceInfo.contact}</p>
                </div>
              </div>
            )}
          </div>


        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-sm opacity-60" style={{ color: maintenanceInfo.textColor }}>
            © 2024 PlayKids Eventos. Todos os direitos reservados.
          </p>
        </div>
      </div>

      {/* Modal de Login */}
      {showLogin && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
            <div className="text-center mb-6">
              <div className="h-16 w-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Acesso Administrativo</h2>
              <p className="text-slate-600">Digite suas credenciais para acessar o sistema</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite seu email"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Senha
                </label>
                <input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Digite sua senha"
                  required
                />
              </div>

              {loginError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm">{loginError}</span>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowLogin(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isLoggingIn}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {isLoggingIn ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Entrar
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Lock className="w-4 h-4 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">Acesso Administrativo</h4>
                  <p className="text-sm text-blue-800">
                    Use suas credenciais de administrador para acessar o sistema durante a manutenção.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenancePage;
